import React, { useState, useEffect, useRef } from 'react';
import './loginv2.css';
import 'antd/dist/antd.min.css';
import { Button, Input, message, Form, Tabs, Divider } from 'antd';
import baseurl from '../../baseurl';
import { request } from '../../util/helper';
import {
  UserOutlined,
  LockOutlined,
  CommentOutlined,
  FileImageOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import AnimatedCharacters from './AnimatedCharacters';

/**
 * 新版竖屏登录页（loginv2）
 * 版式参照 katavii/animated-login 项目（GSAP 动画角色），适配 App 竖屏：
 * 顶部为品牌 + 动画角色区（按屏宽等比缩放），下方为白色登录卡片。
 * 单点登录逻辑与旧版登录页（src/login/logon.js）保持一致：
 *  1. 挂载时检查 redirect 回调地址，已登录则直接跳转（getRedirectUrl）
 *  2. 账号密码 + 图形验证码登录（doLogin），成功后存储 satoken 并跳转 redirectUrl
 *  3. 微信扫码登录（wxloginimg 取码 + querwxAuthorization 每秒轮询，70 秒超时）
 * 旧版登录页 /login 保持原样，可通过页面底部链接或 URL 互相切换。
 */

// 从 URL 查询串中取参数（与旧版登录页一致的实现）
function getParam(name, defaultValue) {
  var query = window.location.search.substring(1);
  var vars = query.split('&');
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=');
    if (pair[0] === name) {
      return pair[1] + (pair[2] ? '=' + pair[2] : '');
    }
  }
  return defaultValue === undefined ? null : defaultValue;
}

function LoginV2() {
  // ─── 微信扫码相关状态（与旧版登录页一致） ───────────────────────────────
  const [Authorization, setAuthorization] = useState({});
  const [Modalauthorimgflag, setModalauthorimgflag] = useState(false);
  const [Modalauthorimgtitle, setModalauthorimgtitle] = useState('加载中');
  // 授权 key 的 ref，避免轮询闭包读到过期的 state
  const AuthorizationRef = useRef({});

  // ─── 动画交互状态 ──────────────────────────────────────────────────────
  const [isTyping, setIsTyping] = useState(false); // 是否正在输入账号
  const [showPassword, setShowPassword] = useState(false); // 是否明文显示密码
  const [passwordValue, setPasswordValue] = useState(''); // 密码值（用于动画判断）
  const [loading, setLoading] = useState(false); // 登录按钮 loading

  // ─── 轮询/超时定时器引用 ────────────────────────────────────────────────
  const IntervalRef = useRef(null);
  const TimeoutRef = useRef(null);

  // ─── 动画角色舞台自适应缩放（App 竖屏适配核心） ─────────────────────────
  // 角色舞台按 550x400 设计，实际按容器宽度等比缩小
  const stageOuterRef = useRef(null);
  const [stageScale, setStageScale] = useState(1);
  useEffect(() => {
    const el = stageOuterRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const vw = window.innerWidth || 0;
      // PC 端保持原项目 1:1 舞台；手机端限制最大缩放 0.7，人物略缩小但保证大部分身体可见
      setStageScale(vw >= 1024 ? Math.min(1, w / 550) : Math.min(0.7, w / 550));
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // 清理扫码轮询定时器
  const clearPolling = () => {
    clearInterval(IntervalRef.current);
    clearTimeout(TimeoutRef.current);
  };

  // 组件卸载时清理定时器，避免内存泄漏
  useEffect(() => () => clearPolling(), []);

  // ─── 单点登录：挂载时检查 redirect，已登录直接跳转（与旧版一致） ─────────
  const intif = () => {
    let redirect = getParam('redirect', '');
    if (redirect !== '') {
      request({
        url: baseurl.login.getRedirectUrl,
        method: 'post',
        data: {
          redirect: getParam('redirect', ''),
          mode: '',
        },
      }).then((res) => {
        console.log(res);
        if (res.code === 200) {
          // 已登录，并且redirect地址有效，开始跳转
          window.location.href = decodeURIComponent(res.data);
        } else if (res.code === 401) {
          console.log('未登录');
          localStorage.removeItem('satoken');
        } else {
          message.error(res.msg);
        }
      });
    } else {
      message.error('无回调地址');
    }
  };

  // ─── 微信扫码授权状态轮询（每秒一次，70 秒超时，与旧版一致） ─────────────
  const authorization = () => {
    clearInterval(IntervalRef.current);
    IntervalRef.current = setInterval(() => {
      request({
        url: baseurl.zhuce.querwxAuthorization,
        method: 'post',
        disloading: true,
        data: {
          key: AuthorizationRef.current.key,
        },
      }).then((res) => {
        if (res.code === 200) {
          let authorizationdata = res.data;
          switch (authorizationdata.state) {
            case '1':
              console.log('已扫描');
              setModalauthorimgflag(false);
              setModalauthorimgtitle('已扫描');
              break;
            case '2':
              // 扫码确认成功，走单点登录跳转流程
              clearInterval(IntervalRef.current);
              clearTimeout(TimeoutRef.current);
              intif();
              break;
            case '3':
              console.log('已取消');
              clearInterval(IntervalRef.current);
              clearTimeout(TimeoutRef.current);
              message.info('您以取消登录');
              break;
            default:
          }
        } else {
          clearInterval(IntervalRef.current);
          clearTimeout(TimeoutRef.current);
          setModalauthorimgflag(false);
          setModalauthorimgtitle('已失效');
        }
      });
    }, 1000);
    clearTimeout(TimeoutRef.current);
    TimeoutRef.current = setTimeout(() => {
      clearInterval(IntervalRef.current);
    }, 70000);
  };

  // ─── 图形验证码刷新（点击图片触发） ─────────────────────────────────────
  const refreshKaptcha = () => {
    let timestamp = new Date().valueOf();
    let kaptcha = document.getElementById('lv2kaptcha');
    if (kaptcha) {
      kaptcha.src = baseurl.login.kaptcha + '?t=' + timestamp;
    }
  };

  // ─── 账号密码登录（doLogin，成功后存 satoken 并跳转，与旧版一致） ─────────
  const loginclick = (values) => {
    let redirect = getParam('redirect', '');
    let { username, password, kaptcha } = values;
    setLoading(true);
    request({
      url:
        baseurl.login.doLogin +
        '?name=' +
        username +
        '&pwd=' +
        password +
        '&kaptcha=' +
        kaptcha +
        '&redirect=' +
        redirect,
      method: 'post',
      data: {},
    }).then((res) => {
      if (res.code === 200) {
        let data = res.data;
        localStorage.setItem('satoken', data.satoken);
        message.success('登录成功');
        setTimeout(function () {
          window.location.href = decodeURIComponent(data.redirectUrl);
        }, 800);
      } else {
        setLoading(false);
        message.error(res.msg);
        // 登录失败刷新验证码
        refreshKaptcha();
      }
    });
  };

  // ─── Tab 切换：切到微信扫码时请求二维码并开始轮询（与旧版一致） ───────────
  const onChange = (key) => {
    if (key === '2') {
      request({
        url: baseurl.zhuce.wxloginimg,
        method: 'post',
        data: {
          redirect: getParam('redirect', ''),
        },
      }).then((res) => {
        if (res.code === 200) {
          AuthorizationRef.current = res.data;
          setAuthorization(res.data);
          setModalauthorimgflag(true);
          setModalauthorimgtitle('');
          authorization();
        } else {
          message.error('系统错误,请刷新后重试');
        }
      });
    } else {
      // 切回账号密码登录，停止轮询并重置状态
      clearPolling();
      AuthorizationRef.current = {};
      setAuthorization({});
      setModalauthorimgflag(false);
      setModalauthorimgtitle('加载中');
    }
  };

  // 挂载时执行单点登录检查（对应旧版 componentDidMount）
  useEffect(() => {
    intif();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切换到旧版登录页时保留 redirect 回调参数
  const redirectParam = getParam('redirect', '');
  const switchOldUrl = '/loginold' + (redirectParam !== '' ? '?redirect=' + redirectParam : '');

  return (
    <div className="lv2-container">
      {/* 左侧：品牌视觉区（PC 为左栏，手机为顶部紧凑区，动画只露上半身） */}
      <div className="lv2-left">
        <div className="lv2-left-top">
          <div className="lv2-brand-mark">
            <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
              <path d="M7 14L12 9L17 14L12 19L7 14Z" fill="white" fillOpacity="0.9" />
              <path d="M13 14L18 9L21 12V16L18 19L13 14Z" fill="white" fillOpacity="0.5" />
            </svg>
          </div>
          <span className="lv2-brand-name">统一认证中心</span>
        </div>

        {/* 动画角色舞台：PC 在左栏底部 500px 展示区（同原项目），手机以设备底边为起始定位向上、露出高度见样式 */}
        <div className="lv2-characters-area" ref={stageOuterRef}>
          <div
            className="lv2-stage-outer"
            style={{ height: 400 * stageScale }}
          >
            <div
              className="lv2-stage"
              style={{ transform: 'scale(' + stageScale + ')' }}
            >
              <AnimatedCharacters
                isTyping={isTyping}
                showPassword={showPassword}
                passwordLength={passwordValue.length}
              />
            </div>
          </div>
        </div>

        <div className="lv2-left-footer">
          <span>This page is provided by yiqiulove.cn</span>
          <a href="https://beian.miit.gov.cn" rel="noopener noreferrer" target="_blank">
            皖ICP备2021012075号
          </a>
        </div>

        {/* 背景装饰：光斑 + 网格 */}
        <div className="lv2-decor-blur1" />
        <div className="lv2-decor-blur2" />
        <div className="lv2-decor-grid" />
      </div>

      {/* 右侧：登录表单（PC 为右栏垂直居中，手机为底部卡片区） */}
      <div className="lv2-right">
        <div className="lv2-form-wrapper">
          <div className="lv2-form-header">
            <h1 className="lv2-form-title">登录</h1>
          </div>

          <Tabs
            centered={true}
            defaultActiveKey="1"
            onChange={onChange}
            className="lv2-tabs"
            items={[
              {
                label: (
                  <span>
                    <LockOutlined />
                    账号密码登录
                  </span>
                ),
                key: '1',
                children: (
                  <Form
                    layout="vertical"
                    name="lv2login"
                    onFinish={loginclick}
                    autoComplete="off"
                    className="lv2-form"
                  >
                    <Form.Item
                      label="用户名"
                      name="username"
                      rules={[
                        {
                          required: true,
                          message: '请输入用户名!',
                        },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined className="lv2-prefix-icon" />}
                        placeholder="输入您的账号"
                        onFocus={() => setIsTyping(true)}
                        onBlur={() => setIsTyping(false)}
                      />
                    </Form.Item>

                    <Form.Item
                      label="密码"
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: '请输入密码!',
                        },
                      ]}
                    >
                      <Input
                        prefix={<LockOutlined className="lv2-prefix-icon" />}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="输入您的密码"
                        onChange={(e) => setPasswordValue(e.target.value)}
                        suffix={
                          <span
                            className="lv2-eye"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          </span>
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <img
                          id="lv2kaptcha"
                          className="lv2-kaptcha-img"
                          src={baseurl.login.kaptcha}
                          alt=""
                          onClick={refreshKaptcha}
                        />
                      }
                      name="kaptcha"
                      rules={[
                        {
                          required: true,
                          message: '请输入验证码!',
                        },
                      ]}
                    >
                      <Input prefix={<FileImageOutlined className="lv2-prefix-icon" />} placeholder="输入验证码" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 8 }}>
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className="lv2-submit-btn"
                      >
                        登录
                      </Button>
                    </Form.Item>
                  </Form>
                ),
              },
              {
                label: (
                  <span>
                    <CommentOutlined />
                    微信扫码登录
                  </span>
                ),
                key: '2',
                children: (
                  <div className="lv2-qr-wrap">
                    <div className="lv2-qr-box">
                      <img alt="" width={200} height={200} src={Authorization.img_base64} />
                      <div className="lv2-qr-overlay">
                        {Modalauthorimgflag ? (
                          <img alt="" width={200} height={200} src="./saoyisao.png" />
                        ) : (
                          <div className="lv2-qr-mask">
                            <span className="lv2-qr-mask-title">{Modalauthorimgtitle}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Divider>打开微信扫一扫以登录</Divider>
                  </div>
                ),
              },
            ]}
          />

          <div className="lv2-signup-row">
            <span>
              没有账户？
              <a className="lv2-signup-link" href="/register">
                点击注册
              </a>
            </span>
            <span className="lv2-signup-divider">|</span>
            <a className="lv2-switch-link" href={switchOldUrl}>
              使用旧版登录
            </a>
          </div>

          <div className="lv2-footer">
            This page is provided by yiqiulove.cn{' '}
            <a href="https://beian.miit.gov.cn" rel="noopener noreferrer" target="_blank">
              皖ICP备2021012075号
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginV2;
